/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    Button,
    Icon,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import React from 'react'

import {iconNames, styleConfigChakraKeys} from '../../constants'

const ButtonType = ({
    onLinkClick,
    type,
    text,
    icon,
    buttonStyles = {},
    iconStyles = {},
    iconComponent,
    children,
    inactive,
    buttonType,
    textUnderLine,
    ...props
}) => {
    const styles = useMultiStyleConfig(styleConfigChakraKeys.buttonType)
    const inactiveClass = inactive ? styles.inactive : {}
    const [isDesktop] = useMediaQuery('(min-width: 992px)')
    return (
        <Button
            onClick={onLinkClick}
            {...inactiveClass}
            {...styles[type]}
            {...buttonStyles}
            {...props}
            isDisabled={props.disabled || props.isDisabled}
            type={buttonType}
            sx={{
                ...(textUnderLine && isDesktop
                    ? {
                          _hover: {
                              fontWeight: 'bold',
                              '&::before': {
                                  width: '0%'
                              }
                          },
                          paddingTop: '25px',
                          paddingBottom: 0,
                          _focus: {
                              backgroundColor: 'transparent'
                          },
                          '&::before': {
                              content: '""',
                              position: 'absolute',
                              bottom: '0',
                              left: '0',
                              width: '100%',
                              height: '2px',
                              backgroundColor: '#000000',
                              transition: 'width 0.3s ease-in-out'
                          }
                      }
                    : !isDesktop && textUnderLine
                    ? {textDecoration: 'underline'}
                    : {})
            }}
        >
            {iconComponent && iconComponent}
            {text && text}
            {icon && <Icon as={iconNames[icon]} {...iconStyles} />}
            {children}
        </Button>
    )
}

ButtonType.displayName = 'Link'

ButtonType.propTypes = {
    onLinkClick: PropTypes.func,
    text: PropTypes.string,
    icon: PropTypes.string,
    buttonStyles: PropTypes.object,
    iconStyles: PropTypes.object,
    iconComponent: PropTypes.object,
    children: PropTypes.node,
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'unstyled']),
    inactive: PropTypes.bool,
    buttonType: PropTypes.oneOf(['button', 'submit', 'reset']),
    disabled: PropTypes.bool,
    isDisabled: PropTypes.bool,
    textUnderLine: PropTypes.bool
}

export default React.memo(ButtonType)
